import React, { useMemo , useState , useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import OTPInput from 'react-otp-input';
import { CheckCircle } from '@phosphor-icons/react';
import Input from '../input/input';
import Dropdown from '../dropdown/dropdown';
import Textarea from '../textarea/textarea';
import Checkbox from '../checkbox/checkbox';
import Radio from '../radio/radio';
import Button from '../button/button';
import { EMAIL_TYPE } from '../../routes/event-create/containers/step-3/constants';
import { sendOtp, resendOtp, verifyOtp } from '../../routes/event-create/api';
import { generateUniqueId } from '../../routes/event-create/containers/step-3/utils';
import { getCurrentUser } from '../../routes/login/api';

import './style';
import { OTP_LENGTH } from '../../constants/common';

const validateEmail = (email) => {
  return email?.match(
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );
};

const componentMap = {
  'Short Text': (props) => {
    const { config, formData, onChange, errors, ...rest } = props;

    return (
      <Input
        label={config?.label}
        testId={`input-${config?.id}`}
        value={formData?.[config?.id]}
        onChange={(e) => onChange(e?.target?.value, config)}
        required={config?.isRequired}
        {...rest}
        width='100%'
        {...errors?.[config?.id] && ({
          validationStatus: 'error',
          helpText: errors?.[config?.id],
        })}
      />
    );
  },
  'Email': (props) => {
    const { config, formData, onChange, errors, editable, ...rest } = props;

    return (
      <Input
        label={config?.label}
        testId={`input-email-${config?.id}`}
        value={formData?.[config?.id]}
        onChange={(e) => onChange(e?.target?.value, config)}
        required={config?.isRequired}
        {...rest}
        disabled={editable}
        width='100%'
        {...errors?.[config?.id] && ({
          validationStatus: 'error',
          helpText: errors?.[config?.id],
        })}
      />
    );
  },
  Dropdown: (props) => {
    const { config, formData, onChange, errors, ...rest } = props;

    const options = useMemo(() => {
      return config?.options?.map((opt) => ({ ...opt, label: opt?.value }));
    }, [config?.options]);

    return (
      <Dropdown
        label={config?.label}
        testId={`dropdown-${config?.id}`}
        value={formData?.[config?.id]}
        options={options}
        onChange={(value) => onChange(value, config)}
        required={config?.isRequired}
        {...rest}
        width='100%'
        {...errors?.[config?.id] && ({
          validationStatus: 'error',
          helpText: errors?.[config?.id],
        })}
      />
    );
  },
  phNumberInput: (props) => {
    const { config, formData, onChange, errors, editable, ...rest } = props;
    const { userData } = useSelector((state) => state.app);
    const [ otpClicked, setOtpClicked ] = useState(false);
    const [ verifiedPh, setVerifiedPh] = useState(false);
    const [ otpText, setOtpText] = useState('');
    const [ error, setError] = useState('');
    const [ phoneNo, setPhoneNo] = useState(formData?.[config?.id]);
    const [resendLinkCount, setIsResendLinkCount] = useState(0);
    const [seconds, setSeconds] = useState(0);

    const interval = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
      if (formData?.[config?.id]) setPhoneNo(formData?.[config?.id]);
    }, [formData?.[config?.id]]);

    const handleVerifyOtp = (otp) => {
      if (!error && otp?.length === OTP_LENGTH) {
        verifyOtp(
          phoneNo,
          otp,
          inputRef.current,
          (data) => {
            if (data?.status === 'success'){
              getCurrentUser();
              setOtpClicked(false);
              setError('');
            } 
            else setError('Please enter correct OTP');
          },
        );
      } else {
        setError('Please enter correct OTP');
      }
    };

    const validateOtp = (otp) => {
      if (otp.length === OTP_LENGTH && /^[0-9]{4}$/.test(otp)) {
        return true;
      }
      return false;
    };

    const handleOtpChange = (otp) => {
      const otpCode = otp.replace(/\D/g, '');
      setOtpText(otpCode);
      setError('');

      if (otpCode.length === OTP_LENGTH && !validateOtp(otpCode)) {
        setError('Please enter correct OTP');
      }

      if (otpCode.length === OTP_LENGTH && validateOtp(otpCode)) {
        setError('');
        setVerifiedPh((prev)=> !prev)
        handleVerifyOtp(otpCode);
      }

    };

    const onClickResend = () => {
       if (seconds === 0) {
        resendOtp(inputRef.current, phoneNo);
        setIsResendLinkCount((prev) => prev + 1);
        setSeconds(30);
        setError('');
      } else {
        console.log('Enter a code to proceed');
      }
    };

    useMemo(() => {
      if (resendLinkCount) {
        interval.current = setInterval(() => {
          if (seconds) setSeconds((prev) => prev - 1);
        }, 1000);
      } else clearInterval(interval.current);
    }, [resendLinkCount]);

    const resendLabel = useMemo(() => {
      if (seconds === 0) {
        setIsResendLinkCount(0);
        return 'Click to resend.';
      }
      if (resendLinkCount) {
        return `Resend in ${seconds} seconds`;
      }
      return 'Click to resend.';
    }, [resendLinkCount, seconds]);

    const handleSendOtp = () =>{
      inputRef.current = generateUniqueId();
      sendOtp(phoneNo, inputRef.current,(data) => {
        if (data?.status === 'success') {
          setError(null);
          setOtpClicked(true);
        } else {
          if (data?.message === 'Invalid Phone number') {
            setError('Please enter a valid mobile number');
            notify('error', 'failed to send OTP!');
          }
        }
      })
    }

    const formatPhoneNumber = (input) => (input?.length === 12  && input?.startsWith("91")) ? input.slice(2) : input;

    return (
      <>
        <div className='relative'>
          <Input
            label={config?.label}
            testId={`number-input-${config?.id}`}
            contentLeft={<>+91</>}
            value={formatPhoneNumber(phoneNo)}
            // disabled={userData?.isPhoneVerified && editable}
            onChange={(e) => {
              setOtpClicked(false);
              setError('')
              onChange(e?.target?.value, config)
              setPhoneNo(e?.target?.value);
            }}
            required={config?.isRequired}
            {...rest}
            width='100%'
            {...errors?.[config?.id] && ({
              validationStatus: 'error',
              helpText: errors?.[config?.id],
            })}
            className="form-ph-number-input"
          />
          {/* {editable && 
            <div className={`right-5 top-8 p-[12px] h-fit flex items-center justify-center absolute`}>
              {!userData?.isPhoneVerified ? (
                <Button
                  className={phoneNo?.length === 10 && !otpClicked?"otp-Btn": "btn-disabled"}
                  type="tertiary-text-underlined"
                  onClick={() =>{
                    if(phoneNo?.length === 10  && !otpClicked){
                      handleSendOtp()
                    }
                  }}
                  disabled={phoneNo?.length!== 10 || otpClicked}
                >
                  Get OTP
                </Button>
                  ) : (
                    <CheckCircle size={24} color='#009D64' weight='fill' />
                )
              }
            </div>
            } */}
        
        </div>

        {otpClicked && 
          (<div>
            <div className= {`label-16 block ${error ? 'text-[#D92D20]' : 'text-grey-70'}`}>
                OTP
            </div>
            <OTPInput
              value={otpText}
              onChange={handleOtpChange}
              isInputNum={true}
              numInputs={OTP_LENGTH}
              renderSeparator={null}
              shouldAutoFocus={true}
              containerStyle="otp-container"
              inputStyle={`otp-input ${error? 'error' : ''}`}
              renderInput={(props) => (
                <input {...props}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  type="tel"
                  value={props.value} />
              )}
            />
            <span className="text-14 flex gap-1 pt-3">
                Didn't get a code?
              <Button
                className={`${seconds !== 0 ? ' btn-disabled' : ''}`}
                children={resendLabel}
                disabled={seconds !== 0}
                type="tertiary-text-underlined"
                onClick={onClickResend}
              />
            </span>
            {error !== "" && (
            <span className="text-14 text-error">
              {error}
            </span>
          )}
          </div>)
        }  
      </>
    );
  },
  'Paragraph text': (props) => {
    const { config, formData, onChange, errors, ...rest } = props;

    return (
      <Textarea
        label={config?.label}
        testId={`number-input-${config?.id}`}
        value={formData?.[config?.id]}
        onChange={(e) => onChange(e?.target?.value, config)}
        required={config?.isRequired}
        {...rest}
        width='100%'
        {...errors?.[config?.id] && ({
          validationStatus: 'error',
          helpText: errors?.[config?.id],
        })}
      />
    );
  },
  'Multiple choice': (props) => {
    const { config, formData, onChange, errors, ...rest } = props;

    const onCheck = (value, others) => {
      if (others) {
        if ((formData?.[config?.id] || []).includes('internal-tmt-others')) {
          const updated = [...(formData?.[config?.id] || [])];
          const idx = updated.indexOf('internal-tmt-others');
          if (idx === updated.length - 1) {
            updated.splice(idx, 1);
          } else {
            updated = updated.slice(0, idx);
          }
          onChange(updated, config);
        } else onChange((formData?.[config?.id] || []).concat('internal-tmt-others'), config);
        return;
      }
      if (formData?.[config?.id]?.includes(value)) {
        const updated = [...(formData?.[config?.id] || [])];
        const idx = updated.indexOf(value);
        updated.splice(idx, 1);
        onChange(updated, config);
      } else {
        const updated = [...(formData?.[config?.id] || [])];
        updated.unshift(value);
        onChange(updated, config);
      }
    };

    const onChangeOthersValue = (e) => {
      if (formData?.[config?.id]?.[formData?.[config?.id].length - 1] === 'internal-tmt-others') {
        onChange((formData?.[config?.id] || []).concat(e.target.value), config)
      } else {
        const updated = [...(formData?.[config?.id] || [])];
        updated[updated.length - 1] = e.target.value;
        onChange(updated, config);
      }
    };

    return (
      <div className='checkbox-group flex flex-col gap-2'>
        <div className={`label-16 ${errors?.[config?.id] ? 'text-error' : 'text-grey-70'} ${config?.isRequired ? 'required' : ''}`}>
          {config?.label}
        </div>
        {config?.options?.map((option) => (
          <div className='flex flex-col gap-2'>
          <Checkbox
            {...option}
            label={option?.others ? 'Others' : option?.value}
            onChange={(value) => onCheck(value, option?.others)}
            checked={(option?.others && formData?.[config?.id]?.includes('internal-tmt-others'))
              ||  formData?.[config?.id]?.includes(option?.id)}
          />
          {(option?.others && formData?.[config?.id]?.includes('internal-tmt-others')) && (
            <Input
              value={formData?.[config?.id]?.[formData?.[config?.id].length - 1] === 'internal-tmt-others' ? '' : formData?.[config?.id]?.[formData?.[config?.id].length - 1]}
              onChange={onChangeOthersValue}
              width="200px"
              className="others-input-field ml-6"
            />
          )}
          </div>
        ))}
        {errors?.[config?.id] && (<div className='text-error text-12'>{errors[config.id]}</div>)}
      </div>
    );
  },
  'Single choice': (props) => {
    const { config, formData, onChange, errors } = props;

    return (
      <div className='radio-group flex flex-col gap-2'>
        <div className={`label-16 ${errors?.[config?.id] ? 'text-error' : 'text-grey-70'} ${config?.isRequired ? 'required' : ''}`}>
          {config?.label}
        </div>
        {config?.options?.map((option) => (
          <Radio
            {...option}
            label={option?.value}
            onChange={(value) => onChange(value, config)}
            checked={formData?.[config?.id]?.includes(option?.id)}
          />
        ))}
        {errors?.[config?.id] && (<div className='text-error text-12'>{errors[config.id]}</div>)}
      </div>
    );
  },
};

const Form = (props) => {
  const {
    formConfig = [],
    ticketCategory,
    value,
    onFormChange,
    errors,
    setErrors,
    disabled,
    attendeesData,
  } = props;

  const { userData } = useSelector((state) => state.app);
  const [modifiedFormConfig, setModifiedFormConfig] = useState(formConfig);

  useEffect(() => {
    if (disabled) {
      const nameExists = modifiedFormConfig?.find((config)=>config?.id === "full_name")  
      const emailExists = modifiedFormConfig?.find((config)=>config?.type === "Email")  
      const phNoField = modifiedFormConfig?.find(config => config?.id === "phone_number")
      let updatedValue = {};
      if(!emailExists){
        setModifiedFormConfig((prev) => prev.concat({
          label: 'Email',
          type: 'Email',
          id: 'email',
          isPreset: false,
          isRequired: true,
        }));
        updatedValue = {
          ...updatedValue,
          [nameExists?.id]: value?.[nameExists?.id] || userData?.name || '',
          email: userData?.email || '',
          [phNoField?.id] : value?.[phNoField?.id] || userData?.mobile?.toString() || '',
        };
      }
      else{
         updatedValue = {
          ...updatedValue,
          [nameExists?.id]: value?.[nameExists?.id] || userData?.name || '',
          [emailExists?.id]: userData?.email || '',
          [phNoField?.id] : value?.[phNoField?.id] || userData?.mobile?.toString() || ''
        };
      }
  
      onFormChange(null, null, updatedValue);
    }
  }, [disabled]);
 
  const onChange = (value, config) => {
    if (errors?.[config?.id]) {
      if (config?.isRequired && value) {
        if (config?.type === EMAIL_TYPE) {
          if (config?.showSpecific) {
            if (config?.options?.some((option) => value?.includes(option?.value))) setErrors((prev) => ({ ...prev, [config?.id]: null }));
          } else if (validateEmail(value)) setErrors((prev) => ({ ...prev, [config?.id]: null }));
        } else if (config?.label === 'Phone Number' && config?.isPreset) {
          if (value?.length === 10 && /^[0-9]{10}$/.test(value)) setErrors((prev) => ({ ...prev, [config?.id]: null }));
        }
      }
    }

    onFormChange(value, config);
  };

  return (
    <div className="form-container flex flex-col gap-4">
      {modifiedFormConfig?.map((config) => {
        if (config?.specificTicketType && config?.ticketTypes?.length && !config?.ticketTypes?.includes(ticketCategory?.ref_id)) {
          return null;
        }

        let Component = componentMap[config?.type];

        if (config?.label === 'Phone Number' && config?.isPreset) Component = componentMap.phNumberInput;

        if (!Component) return null;

        return (
          <>
            <Component
              config={config}
              onChange={onChange}
              formData={value}
              errors={errors}
              editable={disabled}
            />
            {config?.conditions && config?.conditions?.map((condition) => {
              if (value?.[config?.id]?.includes(condition?.choice)) {
                return (
                  <Form
                    formConfig={[condition?.config]}
                    onFormChange={onFormChange}
                    value={value}
                    errors={errors}
                    setErrors={setErrors}
                  />
                );
              }

              return null;
            })}
          </>
        );
      })}
    </div>
  );
};

export default Form;
